import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Calculator, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { formatAmount } from "@/utils/formatAmount";

/**
 * "Licz jako arkusz" — an Excel-status-bar-like inline calculator for amount
 * cells across the tables of a page. While the mode is on:
 *  - every selection is a rectangular BLOCK (a single cell is a 1×1 block):
 *    drag (or Shift+click) selects a range, a plain click selects one cell
 *  - a block is one coherent unit: it renders as one outlined rectangle and
 *    clicking anywhere inside it flips the WHOLE block: added (+) →
 *    subtracted (−) → removed
 *  - a new drag that overlaps existing blocks replaces them (the freshest
 *    selection wins — no cell is ever counted twice)
 *  - the fixed bottom bar shows the per-currency result and lets the user
 *    add/subtract free-hand amounts not present in any table; clicking such
 *    an amount lets the user attach a short note saying what it stands for
 * A rectangle is only the GESTURE: at commit time it is resolved into the
 * stable ids of the cells it covered, so a block sticks to those very rows
 * (invoices) when a filter, a sort or a refetch moves them around — it never
 * slides onto whatever now happens to sit at those positions.
 * With a `storageKey`, mode, blocks and manual amounts survive reloads via
 * localStorage. Deliberately, block cells hidden by filters keep counting
 * (and are restored after F5) until the user clears them — remembering the
 * selection exactly as it was beats silently shrinking the sum.
 */

type Sign = 1 | -1;

interface CellInfo {
  /** Globally unique cell id, e.g. `${tableId}:${rowId}:${col}`. */
  id: string;
  /** Blocks never span tables. */
  tableId: string;
  /** Row position within its table. */
  index: number;
  /** Column position. */
  col?: number;
  value: number;
  currency: string;
}

interface Rect {
  tableId: string;
  rowFrom: number;
  rowTo: number;
  colFrom: number;
  colTo: number;
}

interface Block {
  id: string;
  /** Blocks never span tables. */
  tableId: string;
  sign: Sign;
  /** Stable ids of the selected cells — the block's identity, not its geometry. */
  cells: string[];
}

/** Where a block's cells currently sit, recomputed from what is on screen. */
interface BlockLayout {
  /** `${index}:${col}` of every rendered cell of the block. */
  positions: Set<string>;
  /** Top-left rendered cell — carries the +/− badge. */
  anchor: string;
}

interface ManualEntry {
  id: string;
  value: number;
  sign: Sign;
  /** User-typed label explaining what the amount stands for. */
  note?: string;
}

interface SheetCalcContextValue {
  active: boolean;
  toggle: () => void;
  blockByCell: ReadonlyMap<string, Block>;
  blockLayout: ReadonlyMap<string, BlockLayout>;
  dragRect: Rect | null;
  cellMouseDown: (cell: CellInfo, shiftKey: boolean) => void;
  cellMouseEnter: (cell: CellInfo) => void;
  register: (cell: CellInfo) => () => void;
}

const SheetCalcContext = createContext<SheetCalcContextValue | null>(null);

const EMPTY_MAP = new Map();

const INACTIVE_FALLBACK: SheetCalcContextValue = {
  active: false,
  toggle: () => {},
  blockByCell: EMPTY_MAP,
  blockLayout: EMPTY_MAP,
  dragRect: null,
  cellMouseDown: () => {},
  cellMouseEnter: () => {},
  register: () => () => {},
};

function useSheetCalc(): SheetCalcContextValue {
  return useContext(SheetCalcContext) ?? INACTIVE_FALLBACK;
}

/** Whether calc mode is on — lets hosts with clickable rows suppress their own
 *  row-level click handling while the user is picking amount cells. */
export function useSheetCalcActive(): boolean {
  return useSheetCalc().active;
}

function makeRect(a: CellInfo, b: CellInfo): Rect {
  return {
    tableId: a.tableId,
    rowFrom: Math.min(a.index, b.index),
    rowTo: Math.max(a.index, b.index),
    colFrom: Math.min(a.col ?? 0, b.col ?? 0),
    colTo: Math.max(a.col ?? 0, b.col ?? 0),
  };
}

function inRect(c: CellInfo, r: Rect): boolean {
  return (
    c.tableId === r.tableId &&
    c.index >= r.rowFrom &&
    c.index <= r.rowTo &&
    (c.col ?? 0) >= r.colFrom &&
    (c.col ?? 0) <= r.colTo
  );
}

function posKey(index: number, col?: number): string {
  return `${index}:${col ?? 0}`;
}

/** Reading order — the block's badge goes on its first cell. */
function posBefore(a: string, b: string): boolean {
  const [ai, ac] = a.split(":").map(Number);
  const [bi, bc] = b.split(":").map(Number);
  return ai !== bi ? ai < bi : ac < bc;
}

interface PersistedState {
  active: boolean;
  blocks: Block[];
  cells: CellInfo[];
  manual: ManualEntry[];
}

/** Blocks persisted before selections became id-based were plain rectangles. */
type LegacyBlock = Rect & { id: string; sign: Sign };

function migrateBlocks(blocks: (Block | LegacyBlock)[], cells: CellInfo[]): Block[] {
  return blocks
    .map((b) =>
      Array.isArray((b as Block).cells)
        ? (b as Block)
        : {
            id: (b as LegacyBlock).id,
            tableId: (b as LegacyBlock).tableId,
            sign: (b as LegacyBlock).sign,
            cells: cells
              .filter((c) => inRect(c, b as LegacyBlock))
              .map((c) => c.id),
          },
    )
    .filter((b) => b.cells.length > 0);
}

function loadPersisted(storageKey: string | undefined): PersistedState | null {
  if (!storageKey) return null;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedState;
    if (
      !Array.isArray(parsed.blocks) ||
      !Array.isArray(parsed.cells) ||
      !Array.isArray(parsed.manual)
    ) {
      return null;
    }
    return { ...parsed, blocks: migrateBlocks(parsed.blocks, parsed.cells) };
  } catch {
    return null;
  }
}

export function SheetCalcProvider({
  children,
  storageKey,
}: {
  children: React.ReactNode;
  /** localStorage key — pass to persist mode + selection across reloads. */
  storageKey?: string;
}) {
  const [active, setActive] = useState(
    () => loadPersisted(storageKey)?.active ?? false,
  );
  const [blocks, setBlocks] = useState<Block[]>(
    () => loadPersisted(storageKey)?.blocks ?? [],
  );
  // Value snapshot of every cell covered by a block. The sum is computed from
  // this cache, not the live registry, so amounts survive reloads and cells
  // hidden by filters keep counting until their block is removed.
  const [cellCache, setCellCache] = useState<Map<string, CellInfo>>(() => {
    const cells = loadPersisted(storageKey)?.cells ?? [];
    return new Map(cells.map((c) => [c.id, c]));
  });
  const [manual, setManual] = useState<ManualEntry[]>(
    () => loadPersisted(storageKey)?.manual ?? [],
  );
  // Live registry of every calc-enabled cell currently rendered. Instance-counted
  // because the same table can be mounted twice (inline + mobile landscape modal).
  const registryRef = useRef<Map<string, { cell: CellInfo; count: number }>>(
    new Map(),
  );
  // Bumped on every mount/unmount so the on-screen layout of blocks is recomputed.
  const [registryVersion, setRegistryVersion] = useState(0);
  const blockByCell = useMemo(() => {
    const map = new Map<string, Block>();
    for (const b of blocks) for (const id of b.cells) map.set(id, b);
    return map;
  }, [blocks]);
  const blockByCellRef = useRef(blockByCell);
  blockByCellRef.current = blockByCell;
  // Id of the last individually clicked cell = anchor for the next Shift+click
  // range. Kept as an id, so the range spans its CURRENT row even if the table
  // was re-filtered or re-sorted in between.
  const anchorRef = useRef<string | null>(null);
  const dragStartRef = useRef<CellInfo | null>(null);
  const dragEndRef = useRef<CellInfo | null>(null);
  const [dragRect, setDragRect] = useState<Rect | null>(null);

  useEffect(() => {
    if (!storageKey) return;
    try {
      const state: PersistedState = {
        active,
        blocks,
        cells: [...cellCache.values()],
        manual,
      };
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
      // Storage full/unavailable — the calculator still works, just won't survive F5.
    }
  }, [storageKey, active, blocks, cellCache, manual]);

  // Blocks are the source of truth — drop cached values no block covers anymore.
  useEffect(() => {
    setCellCache((prev) => {
      const next = new Map<string, CellInfo>();
      for (const [id, c] of prev) {
        if (blockByCell.has(id)) next.set(id, c);
      }
      return next.size === prev.size ? prev : next;
    });
  }, [blockByCell]);

  // Positions come from the live registry, never from the value cache: a row
  // hidden by a filter must not leave a ghost position that would punch a hole
  // in the outline drawn around the part of the block that is still visible.
  const blockLayout = useMemo(() => {
    void registryVersion;
    const map = new Map<string, BlockLayout>();
    for (const { cell } of registryRef.current.values()) {
      const block = blockByCell.get(cell.id);
      if (!block) continue;
      const key = posKey(cell.index, cell.col);
      const entry = map.get(block.id);
      if (!entry) {
        map.set(block.id, { positions: new Set([key]), anchor: key });
        continue;
      }
      entry.positions.add(key);
      if (posBefore(key, entry.anchor)) entry.anchor = key;
    }
    return map;
  }, [blockByCell, registryVersion]);

  const toggle = useCallback(() => {
    setActive((prev) => {
      if (prev) {
        setBlocks([]);
        setManual([]);
        anchorRef.current = null;
      }
      return !prev;
    });
  }, []);

  const register = useCallback((cell: CellInfo) => {
    const entry = registryRef.current.get(cell.id);
    registryRef.current.set(cell.id, { cell, count: (entry?.count ?? 0) + 1 });
    setRegistryVersion((v) => v + 1);
    // Keep the cache in sync with what the user actually sees: a selected cell
    // refreshes its snapshot (new value after refetch, new position after sort).
    setCellCache((prev) => {
      const covered = blockByCellRef.current.has(cell.id);
      const current = prev.get(cell.id);
      if (covered) {
        if (
          current &&
          current.value === cell.value &&
          current.currency === cell.currency &&
          current.index === cell.index &&
          (current.col ?? 0) === (cell.col ?? 0)
        ) {
          return prev;
        }
        const next = new Map(prev);
        next.set(cell.id, cell);
        return next;
      }
      if (!current) return prev;
      const next = new Map(prev);
      next.delete(cell.id);
      return next;
    });
    return () => {
      const reg = registryRef.current.get(cell.id);
      if (!reg) return;
      if (reg.count <= 1) registryRef.current.delete(cell.id);
      else registryRef.current.set(cell.id, { ...reg, count: reg.count - 1 });
      setRegistryVersion((v) => v + 1);
    };
  }, []);

  const snapshotCells = useCallback((ids: string[]) => {
    if (!ids.length) return;
    setCellCache((prev) => {
      let next: Map<string, CellInfo> | null = null;
      for (const id of ids) {
        const reg = registryRef.current.get(id);
        if (!reg) continue;
        if (!next) next = new Map(prev);
        next.set(id, reg.cell);
      }
      return next ?? prev;
    });
  }, []);

  /** Turn the dragged rectangle into the ids of the cells it actually covers. */
  const idsInRect = useCallback((rect: Rect) => {
    const ids: string[] = [];
    for (const { cell } of registryRef.current.values()) {
      if (inRect(cell, rect)) ids.push(cell.id);
    }
    return ids;
  }, []);

  const selectCells = useCallback(
    (tableId: string, ids: string[]) => {
      if (!ids.length) return;
      const picked = new Set(ids);
      // The freshest selection wins: blocks sharing any cell with the new one
      // dissolve, so no cell can ever be counted twice.
      setBlocks((prev) => [
        ...prev.filter((b) => !b.cells.some((id) => picked.has(id))),
        { id: crypto.randomUUID(), tableId, sign: 1, cells: ids },
      ]);
      snapshotCells(ids);
    },
    [snapshotCells],
  );

  const commitRect = useCallback(
    (rect: Rect) => selectCells(rect.tableId, idsInRect(rect)),
    [idsInRect, selectCells],
  );

  // Click = flip the whole block under the cursor: + → − → removed.
  // Clicking an unselected cell starts a fresh single-cell block.
  const clickCell = useCallback(
    (cell: CellInfo) => {
      const hit = blockByCellRef.current.get(cell.id);
      if (!hit) {
        selectCells(cell.tableId, [cell.id]);
        return;
      }
      setBlocks((prev) =>
        hit.sign === 1
          ? prev.map((b) => (b.id === hit.id ? { ...b, sign: -1 as Sign } : b))
          : prev.filter((b) => b.id !== hit.id),
      );
    },
    [selectCells],
  );

  const cellMouseDown = useCallback(
    (cell: CellInfo, shiftKey: boolean) => {
      const anchorId = anchorRef.current;
      // An anchor scrolled out of the current filter can't define a range.
      const anchor = anchorId
        ? (registryRef.current.get(anchorId)?.cell ?? null)
        : null;
      if (shiftKey && anchor && anchor.tableId === cell.tableId) {
        commitRect(makeRect(anchor, cell));
        return;
      }
      dragStartRef.current = cell;
      dragEndRef.current = null;
    },
    [commitRect],
  );

  const cellMouseEnter = useCallback((cell: CellInfo) => {
    const start = dragStartRef.current;
    if (!start || cell.tableId !== start.tableId) return;
    if (cell.id === start.id && !dragEndRef.current) return;
    dragEndRef.current = cell;
    setDragRect(makeRect(start, cell));
  }, []);

  // Mouseup anywhere ends the gesture: a real drag commits the rectangle,
  // a click-in-place flips the block under the cursor.
  useEffect(() => {
    if (!active) return;
    const onUp = () => {
      const start = dragStartRef.current;
      if (!start) return;
      const end = dragEndRef.current;
      if (end && end.id !== start.id) commitRect(makeRect(start, end));
      else clickCell(start);
      anchorRef.current = start.id;
      dragStartRef.current = null;
      dragEndRef.current = null;
      setDragRect(null);
    };
    window.addEventListener("mouseup", onUp);
    return () => window.removeEventListener("mouseup", onUp);
  }, [active, commitRect, clickCell]);

  const value = useMemo(
    () => ({
      active,
      toggle,
      blockByCell,
      blockLayout,
      dragRect,
      cellMouseDown,
      cellMouseEnter,
      register,
    }),
    [
      active,
      toggle,
      blockByCell,
      blockLayout,
      dragRect,
      cellMouseDown,
      cellMouseEnter,
      register,
    ],
  );

  return (
    <SheetCalcContext.Provider value={value}>
      {children}
      {active && (
        <SheetCalcBar
          blockByCell={blockByCell}
          cellCache={cellCache}
          manual={manual}
          onAddManual={(value, sign) =>
            setManual((prev) => [
              ...prev,
              { id: crypto.randomUUID(), value, sign },
            ])
          }
          onRemoveManual={(id) =>
            setManual((prev) => prev.filter((m) => m.id !== id))
          }
          onNoteManual={(id, note) =>
            setManual((prev) =>
              prev.map((m) =>
                m.id === id ? { ...m, note: note || undefined } : m,
              ),
            )
          }
          onClear={() => {
            setBlocks([]);
            setManual([]);
          }}
          onClose={toggle}
        />
      )}
    </SheetCalcContext.Provider>
  );
}

export function SheetCalcToggleButton({
  size,
  tone = "default",
}: {
  size?: "sm" | "md" | "lg";
  /** "excel": zielony jak Excel; włączony tryb = ciemniejszy, „wciśnięty". */
  tone?: "default" | "excel";
}) {
  const { active, toggle } = useSheetCalc();
  if (tone === "excel") {
    return (
      <Button
        variant="excel"
        size={size}
        onClick={toggle}
        aria-pressed={active}
        className={active ? "!bg-[#0e4f2a] !border-[#0e4f2a] shadow-[inset_0_2px_4px_rgba(0,0,0,0.35)]" : undefined}
      >
        <Calculator size={15} className="mr-1.5" />
        Licz jako arkusz
      </Button>
    );
  }
  return (
    <Button variant={active ? "primary" : "secondary"} size={size} onClick={toggle}>
      <Calculator size={15} className="mr-1.5" />
      Licz jako arkusz
    </Button>
  );
}

function SheetCalcBar({
  blockByCell,
  cellCache,
  manual,
  onAddManual,
  onRemoveManual,
  onNoteManual,
  onClear,
  onClose,
}: {
  blockByCell: ReadonlyMap<string, Block>;
  cellCache: ReadonlyMap<string, CellInfo>;
  manual: ManualEntry[];
  onAddManual: (value: number, sign: Sign) => void;
  onRemoveManual: (id: string) => void;
  onNoteManual: (id: string, note: string) => void;
  onClear: () => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState("");
  // Amounts are summed per currency — mixing EUR into a PLN total would be
  // silently wrong, so each currency gets its own result. Manual amounts count as PLN.
  const sums = new Map<string, number>();
  let cellCount = 0;
  for (const cell of cellCache.values()) {
    const block = blockByCell.get(cell.id);
    if (!block) continue;
    cellCount += 1;
    sums.set(cell.currency, (sums.get(cell.currency) ?? 0) + block.sign * cell.value);
  }
  for (const m of manual) {
    sums.set("PLN", (sums.get("PLN") ?? 0) + m.sign * m.value);
  }
  const parts = [...sums.entries()].map(
    ([currency, sum]) => `${formatAmount(sum)} ${currency}`,
  );
  const count = cellCount + manual.length;

  const submitManual = (sign: Sign) => {
    const value = parseFloat(draft.replace(/\s/g, "").replace(",", "."));
    if (!isFinite(value) || value === 0) return;
    onAddManual(Math.abs(value), sign);
    setDraft("");
  };

  return (
    <div className="fixed bottom-0 inset-x-0 z-40 border-t border-gray-200 dark:border-gray-600 bg-white/95 dark:bg-gray-800/95 backdrop-blur px-4 py-2.5 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]">
      <div className="max-w-7xl mx-auto flex items-center gap-x-4 gap-y-2 flex-wrap">
        <Calculator size={16} className="text-[var(--color-primary)] shrink-0" />
        {count === 0 ? (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Klikaj kwoty albo przeci&#261;gnij mysz&#261; po zakresie &middot; klik
            na zaznaczeniu prze&#322;&#261;cza je: + &rarr; &minus; &rarr; usuni&#281;cie
          </span>
        ) : (
          <>
            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
              Pozycje: {count}
            </span>
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 tabular-nums whitespace-nowrap">
              = {parts.join(" · ")}
            </span>
          </>
        )}
        {manual.map((m) => (
          <ManualChip
            key={m.id}
            entry={m}
            onRemove={() => onRemoveManual(m.id)}
            onNote={(note) => onNoteManual(m.id, note)}
          />
        ))}
        <span className="inline-flex items-center gap-1">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitManual(1);
            }}
            placeholder="kwota z ręki"
            inputMode="decimal"
            className="w-28 text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
          />
          <button
            onClick={() => submitManual(1)}
            className="text-xs font-bold w-6 h-6 rounded border border-emerald-300 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30"
            title="Dodaj kwotę do wyniku"
          >
            +
          </button>
          <button
            onClick={() => submitManual(-1)}
            className="text-xs font-bold w-6 h-6 rounded border border-rose-300 text-rose-700 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30"
            title="Odejmij kwotę od wyniku"
          >
            −
          </button>
        </span>
        {count > 0 && (
          <button
            onClick={onClear}
            className="text-xs text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:underline"
          >
            Wyczyść
          </button>
        )}
        <button
          onClick={onClose}
          className="ml-auto text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          title="Zakończ liczenie"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

/** A free-hand amount in the bar. Clicking it opens an inline input for a note
 *  ("what is this amount?") — committed on Enter/blur, cancelled on Escape,
 *  cleared by committing an empty text. */
function ManualChip({
  entry,
  onRemove,
  onNote,
}: {
  entry: ManualEntry;
  onRemove: () => void;
  onNote: (note: string) => void;
}) {
  // null = not editing; otherwise the note text being typed.
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft !== null) onNote(draft.trim());
    setDraft(null);
  };
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${entry.sign === 1 ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" : "bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"}`}
    >
      <button
        onClick={() => setDraft(entry.note ?? "")}
        className="inline-flex items-center gap-1 hover:opacity-70"
        title={entry.note ? "Zmień opis kwoty" : "Dodaj opis kwoty"}
      >
        <span className="tabular-nums">
          {entry.sign === 1 ? "+" : "−"}
          {formatAmount(entry.value)}
        </span>
        {draft === null && entry.note && (
          <span className="max-w-[12rem] truncate font-normal opacity-80">
            &middot; {entry.note}
          </span>
        )}
      </button>
      {draft !== null && (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") setDraft(null);
          }}
          placeholder="opis"
          className="w-32 text-xs bg-transparent border-b border-current focus:outline-none placeholder:text-current placeholder:opacity-50"
        />
      )}
      <button onClick={onRemove} className="hover:opacity-70" title="Usuń kwotę">
        <X size={11} />
      </button>
    </span>
  );
}

interface CalcCellProps extends CellInfo {
  children: React.ReactNode;
}

/** Per-edge border classes so a group of adjacent selected cells reads as ONE
 *  rectangle: only its outer edges are drawn, inner cells get just the fill.
 *  `member` answers "is that neighbouring position part of the same selection?",
 *  so a block split apart by a filter is outlined as the pieces now visible. */
function selectionEdgeClasses(
  cell: CellInfo,
  palette: "plus" | "minus" | "drag",
  member: (index: number, col: number) => boolean,
  single: boolean,
): string {
  const colors = {
    plus: "border-t-emerald-500 border-b-emerald-500 border-l-emerald-500 border-r-emerald-500 bg-emerald-50 dark:bg-emerald-900/30",
    minus: "border-t-rose-500 border-b-rose-500 border-l-rose-500 border-r-rose-500 bg-rose-50 dark:bg-rose-900/30",
    drag: "border-t-[var(--color-primary)] border-b-[var(--color-primary)] border-l-[var(--color-primary)] border-r-[var(--color-primary)] bg-blue-50 dark:bg-blue-900/30",
  }[palette].split(" ");
  const [top, bottom, left, right, ...bg] = colors;
  const row = cell.index;
  const col = cell.col ?? 0;
  const cls = [...bg];
  if (!member(row - 1, col)) cls.push(top);
  if (!member(row + 1, col)) cls.push(bottom);
  if (!member(row, col - 1)) cls.push(left);
  if (!member(row, col + 1)) cls.push(right);
  cls.push(single ? "rounded" : "rounded-none");
  return cls.join(" ");
}

/** Wraps an amount cell's content, making it clickable while calc mode is on. */
export function CalcCell({ children, ...cell }: CalcCellProps) {
  const {
    active,
    blockByCell,
    blockLayout,
    dragRect,
    cellMouseDown,
    cellMouseEnter,
    register,
  } = useSheetCalc();
  const { id, tableId, index, col, value, currency } = cell;
  useEffect(
    () => register({ id, tableId, index, col, value, currency }),
    [register, id, tableId, index, col, value, currency],
  );
  if (!active) return <>{children}</>;
  const block = blockByCell.get(cell.id);
  const layout = block ? blockLayout.get(block.id) : undefined;
  const inDragPreview = dragRect != null && inRect(cell, dragRect);
  const stateClasses = inDragPreview
    ? selectionEdgeClasses(
        cell,
        "drag",
        (i, c) => inRect({ ...cell, index: i, col: c }, dragRect),
        dragRect.rowFrom === dragRect.rowTo && dragRect.colFrom === dragRect.colTo,
      )
    : block
      ? selectionEdgeClasses(
          cell,
          block.sign === 1 ? "plus" : "minus",
          (i, c) => layout?.positions.has(posKey(i, c)) ?? false,
          (layout?.positions.size ?? 1) === 1,
        )
      : "rounded hover:border-[var(--color-primary)] hover:bg-blue-50/50 dark:hover:bg-blue-900/20";
  // The sign badge sits once per block, on its first still-visible cell.
  const showBadge =
    !!block && !inDragPreview && layout?.anchor === posKey(cell.index, cell.col);
  return (
    <div
      onMouseDown={(e) => {
        if (e.button !== 0) return;
        // Native drag-selection of text would fight the range gesture.
        e.preventDefault();
        cellMouseDown(cell, e.shiftKey);
      }}
      onMouseEnter={() => cellMouseEnter(cell)}
      className={`cursor-pointer select-none px-1 -mx-1 border border-transparent transition-colors ${stateClasses}`}
    >
      {showBadge && (
        <span
          className={`mr-1 font-bold ${block!.sign === 1 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}
        >
          {block!.sign === 1 ? "+" : "−"}
        </span>
      )}
      {children}
    </div>
  );
}
